import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";

const navLinks = [
  { label: "Home", to: "/" },
  {
    label: "Courses",
    children: [
      { label: "All Courses", to: "/courses" },
      { label: "🔥 Course Packages (₹499)", to: "/course-packages" },
      { label: "Curriculum (24 Weeks)", to: "/curriculum" },
      { label: "Basic Course Brochure", to: "/basic-course-brochure" },
    ],
  },
  { label: "Services", to: "/services" },
  { label: "Blog", to: "/blog" },
  { label: "Pricing", to: "/pricing" },
  { label: "Success Stories", to: "/success-stories" },
  {
    label: "More",
    children: [
      { label: "Career Opportunities", to: "/opportunities" },
      { label: "Our Vision", to: "/vision" },
      { label: "FAQs", to: "/faq" },
      { label: "Awareness Test", to: "/awareness-test" },
      { label: "🧠 Job Preparation", to: "/job-preparation" },
      { label: "📚 Learning Library", to: "/learning-library" },
      { label: "📝 Prompt Library", to: "/prompt-library" },
      { label: "🎨 AI Thumbnail Generator", to: "/thumbnail-generator" },
      { label: "📊 Dashboard", to: "/dashboard" },
    ],
  },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/50">
      <div className="container mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="font-bold text-xl tracking-tight">
          <span className="text-gradient">DigiAd</span>
          <span className="text-foreground">Words</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(link.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors rounded-lg"
                  aria-expanded={openDropdown === link.label}
                  aria-haspopup="true"
                  aria-label={`${link.label} menu`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
                <AnimatePresence>
                  {openDropdown === link.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 min-w-[220px] rounded-xl bg-card border border-border shadow-neon p-2"
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                            isActive(child.to)
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to!}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.to!)
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/free-workshop">
            <Button variant="outline" size="sm" className="rounded-full border-primary/30 text-primary hover:bg-primary/10">
              Free Workshop
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="rounded-full glow-green">
              Enroll Now
            </Button>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2 text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-border bg-background overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      onClick={() =>
                        setOpenDropdown(openDropdown === link.label ? null : link.label)
                      }
                      className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-muted-foreground rounded-lg"
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          openDropdown === link.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <AnimatePresence>
                      {openDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-4 space-y-1 overflow-hidden"
                        >
                          {link.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setMobileOpen(false)}
                              className={`block px-4 py-2.5 text-sm rounded-lg ${
                                isActive(child.to)
                                  ? "text-primary bg-primary/10 font-medium"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to!}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-4 py-3 text-sm font-medium rounded-lg ${
                      isActive(link.to!)
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}

              <div className="pt-4 flex flex-col gap-2 border-t border-border mt-2">
                <Link to="/free-workshop" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full border-primary/30 text-primary">
                    Free Workshop
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full rounded-full glow-green">Enroll Now</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
